import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, DocumentTextIcon, LinkIcon, PencilIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { DocumentDetail, Category, DocumentVersion } from '../types/documentVersion.types';
import { DocumentTrackingPanel } from './DocumentTrackingPanel';
import {
  getMatterPhaseLabel,
  listAllDocumentTypeOptions,
  listDocumentTypeOptionsForPracticeAreas,
  listMatterPhaseOptions,
  resolveDocumentTypeKey,
} from '../../../taxonomy/legalDocumentTaxonomy';

interface DocumentMetadataProps {
  document: DocumentDetail;
  versions?: DocumentVersion[];
  contacts: any[];
  matters: any[];
  categories: Category[];
  isEditingTitle: boolean;
  isEditingClient: boolean;
  isEditingMatter: boolean;
  isEditingCategory: boolean;
  isEditingPhase: boolean;
  isUpdatingDocument: boolean;
  onTitleUpdate: (title: string) => Promise<void>;
  onClientUpdate: (clientId: string | null) => Promise<void>;
  onMatterUpdate: (matterId: string | null) => Promise<void>;
  onCategoryUpdate: (categoryId: string | null) => Promise<void>;
  onPhaseUpdate: (phaseKey: string | null) => Promise<void>;
  setIsEditingTitle: (editing: boolean) => void;
  setIsEditingClient: (editing: boolean) => void;
  setIsEditingMatter: (editing: boolean) => void;
  setIsEditingCategory: (editing: boolean) => void;
  setIsEditingPhase: (editing: boolean) => void;
  fetchContacts: () => void;
  fetchMatters: () => void;
  fetchCategories: () => Promise<void>;
}

export const DocumentMetadata: React.FC<DocumentMetadataProps> = ({
  document,
  versions = [],
  contacts,
  matters,
  categories,
  isEditingTitle,
  isEditingClient,
  isEditingMatter,
  isEditingCategory,
  isEditingPhase,
  isUpdatingDocument,
  onTitleUpdate,
  onClientUpdate,
  onMatterUpdate,
  onCategoryUpdate,
  onPhaseUpdate,
  setIsEditingTitle,
  setIsEditingClient,
  setIsEditingMatter,
  setIsEditingCategory,
  setIsEditingPhase,
  fetchContacts,
  fetchMatters,
  fetchCategories,
}) => {
  const [titleDraft, setTitleDraft] = useState(document.title || '');

  useEffect(() => {
    if (!isEditingTitle) {
      setTitleDraft(document.title || '');
    }
  }, [document.title, isEditingTitle]);

  const versionList = useMemo(() => {
    return versions.length > 0 ? versions : document.versions || [];
  }, [document.versions, versions]);

  const latestVersion = useMemo(() => {
    if (document.latest_version) return document.latest_version as any;
    return [...versionList].sort((a, b) => {
      const aTime = new Date(a.checked_in_at || a.uploaded_at || a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.checked_in_at || b.uploaded_at || b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    })[0];
  }, [document.latest_version, versionList]);

  const selectedClient = useMemo(() => {
    if (!document.client_id) return null;
    return contacts.find((contact) => contact.id === document.client_id || contact.client_id === document.client_id) || null;
  }, [contacts, document.client_id]);

  const selectedMatter = useMemo(() => {
    if (!document.matter_id) return null;
    return matters.find((matter) => matter.id === document.matter_id || matter.matter_id === document.matter_id) || null;
  }, [document.matter_id, matters]);

  const selectedCategory = useMemo(() => {
    if (!document.category_id) return null;
    return categories.find((category) => category.id === document.category_id) || null;
  }, [categories, document.category_id]);

  const selectedDocumentTypeKey = useMemo(() => {
    return resolveDocumentTypeKey(document.primary_type || document.category_name || selectedCategory?.name || null);
  }, [document.category_name, document.primary_type, selectedCategory?.name]);

  const documentTypeOptions = useMemo(() => {
    const practiceArea = selectedMatter?.practice_area || selectedMatter?.practiceArea || null;
    const scopedOptions = practiceArea
      ? listDocumentTypeOptionsForPracticeAreas([practiceArea])
      : listAllDocumentTypeOptions();
    if (!selectedDocumentTypeKey || scopedOptions.some((option) => option.value === selectedDocumentTypeKey)) {
      return scopedOptions;
    }
    const currentOption = listAllDocumentTypeOptions().find((option) => option.value === selectedDocumentTypeKey);
    return currentOption ? [currentOption, ...scopedOptions] : scopedOptions;
  }, [selectedDocumentTypeKey, selectedMatter?.practiceArea, selectedMatter?.practice_area]);

  const phaseOptions = useMemo(() => listMatterPhaseOptions(), []);
  const selectedDocumentTypeOption = documentTypeOptions.find((option) => option.value === selectedDocumentTypeKey) || null;
  const legalDocumentType = selectedDocumentTypeOption?.label || document.category_name || selectedCategory?.name || null;
  const legalDocumentDescription = selectedDocumentTypeOption
    ? `${selectedDocumentTypeOption.phaseLabel}${selectedDocumentTypeOption.universal ? ' · universal' : ''}`
    : document.category_description || selectedCategory?.description || null;
  const phaseLabel = getMatterPhaseLabel(document.matter_phase || selectedDocumentTypeOption?.phaseKey || null);
  const clientDisplay = document.client_name || selectedClient?.name || null;
  const matterDisplay = document.matter_description || selectedMatter?.description || selectedMatter?.matter_number || null;
  const matterMeta = selectedMatter?.matter_number && selectedMatter?.description
    ? selectedMatter.matter_number
    : null;
  const isCourtRulesDocument = selectedDocumentTypeKey === 'court_rules';
  const aiRetrievalScopeLabel = document.matter_id
    ? 'Matter-scoped'
    : isCourtRulesDocument
      ? 'Court Rules authority'
      : 'Unassigned document';
  const aiRetrievalScopeClass = document.matter_id
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    : isCourtRulesDocument
      ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  const sourceFileName = latestVersion?.original_name || document.title;
  const fileType = describeFileType(latestVersion?.mime_type, sourceFileName, document.type);
  const latestVersionLabel = latestVersion?.version_number ? `v${String(latestVersion.version_number).replace(/^v/i, '')}` : 'No version recorded';
  const modifiedAt = document.effective_modified_at || latestVersion?.checked_in_at || latestVersion?.updated_at || latestVersion?.uploaded_at || document.updated_at;
  const status = String(document.status || 'Unknown');
  const normalizedStatus = status.toLowerCase();
  const hasMatterMismatch = Boolean(document.matter_id && matterDisplay === null);
  const hasClientMismatch = Boolean(document.client_id && clientDisplay === null);

  // Load data when editing starts
  useEffect(() => {
    if (isEditingClient && contacts.length === 0) {
      fetchContacts();
    }
  }, [isEditingClient, contacts.length, fetchContacts]);

  useEffect(() => {
    if (isEditingMatter && matters.length === 0) {
      fetchMatters();
    }
  }, [isEditingMatter, matters.length, fetchMatters]);

  useEffect(() => {
    if (isEditingCategory && categories.length === 0) {
      fetchCategories();
    }
  }, [isEditingCategory, categories.length, fetchCategories]);

  return (
    <div className="min-w-0 max-w-full space-y-6">
      <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--brand-accent-primary)]">
              <ShieldCheckIcon className="h-4 w-4" />
              Authoritative Ledger
            </div>
            <h4 className="mt-2 text-lg font-semibold text-[var(--brand-text-primary)]">Document Information</h4>
            <p className="mt-1 max-w-3xl text-sm text-[var(--brand-text-secondary)]">
              Verified DMS record, legal classification, matter linkage, latest version, and chain-of-custody status.
            </p>
          </div>
          <div className="flex max-w-full flex-wrap gap-2 text-xs text-[var(--brand-text-secondary)]">
            <VerificationBadge label="DMS record" verified={Boolean(document.document_id)} />
            <VerificationBadge label="Version source" verified={Boolean(latestVersion)} />
            <VerificationBadge label="Matter scope" verified={Boolean(document.matter_id && matterDisplay)} warning={!document.matter_id || hasMatterMismatch} />
          </div>
        </div>
      </div>
      
      <div className="grid min-w-0 gap-4 xl:grid-cols-3">
        <InfoCard title="Record" icon={<DocumentTextIcon className="h-5 w-5" />}>
          <InfoRow label="Title">
            {isEditingTitle ? (
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  onTitleUpdate(titleDraft);
                }}
              >
                <input
                  className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:border-transparent focus:ring-2 focus:ring-lex-accent"
                  value={titleDraft}
                  onChange={(event) => setTitleDraft(event.target.value)}
                  disabled={isUpdatingDocument}
                />
                <InlineEditorActions
                  onCancel={() => {
                    setTitleDraft(document.title || '');
                    setIsEditingTitle(false);
                  }}
                  disabled={isUpdatingDocument}
                  saveDisabled={!titleDraft.trim()}
                />
              </form>
            ) : (
              <EditableValue
                value={document.title || 'Untitled document'}
                disabled={isUpdatingDocument}
                verified={Boolean(document.title)}
                onEdit={() => setIsEditingTitle(true)}
              />
            )}
          </InfoRow>
          <InfoRow label="Record Status">
            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
              normalizedStatus === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' :
              normalizedStatus === 'archived' || normalizedStatus === 'deleted' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' :
              'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
            }`}>
              {status}
            </span>
          </InfoRow>
          <InfoRow label="Created" value={formatDateTime(document.created_at)} verified={Boolean(document.created_at)} />
          <InfoRow label="Latest DMS Activity" value={formatDateTime(modifiedAt)} verified={Boolean(modifiedAt)} />
          <InfoRow label="Created By" value={document.created_by_name || 'Unknown'} verified={Boolean(document.created_by_name)} />
        </InfoCard>

        <InfoCard title="Classification" icon={<DocumentTextIcon className="h-5 w-5" />}>
          <InfoRow label="Legal Document Type">
            {isEditingCategory ? (
              <div className="space-y-2">
                <select
                  className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:border-transparent focus:ring-2 focus:ring-lex-accent"
                  value={selectedDocumentTypeKey || ''}
                  onChange={(e) => onCategoryUpdate(e.target.value || null)}
                  disabled={isUpdatingDocument}
                >
                  <option value="">No Document Type</option>
                  {documentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <InlineCancel onClick={() => setIsEditingCategory(false)} disabled={isUpdatingDocument} />
              </div>
            ) : (
              <EditableValue
                value={legalDocumentType || 'No Document Type'}
                detail={legalDocumentDescription || 'Used for DMS organization, retrieval filters, and AI context.'}
                warning={!legalDocumentType}
                disabled={isUpdatingDocument}
                onEdit={() => setIsEditingCategory(true)}
              />
            )}
          </InfoRow>
          <InfoRow label="Matter Phase">
            {isEditingPhase ? (
              <div className="space-y-2">
                <select
                  className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:border-transparent focus:ring-2 focus:ring-lex-accent"
                  value={document.matter_phase || selectedDocumentTypeOption?.phaseKey || ''}
                  onChange={(e) => onPhaseUpdate(e.target.value || null)}
                  disabled={isUpdatingDocument}
                >
                  <option value="">No Matter Phase</option>
                  {phaseOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <InlineCancel onClick={() => setIsEditingPhase(false)} disabled={isUpdatingDocument} />
              </div>
            ) : (
              <EditableValue
                value={phaseLabel || 'No Matter Phase'}
                detail="Canonical phase used by DMS organization, playbooks, and agentic retrieval."
                warning={!phaseLabel}
                disabled={isUpdatingDocument}
                onEdit={() => setIsEditingPhase(true)}
              />
            )}
          </InfoRow>
          <InfoRow
            label="Taxonomy Version"
            value={document.classification_version || 'Not recorded'}
            verified={Boolean(document.classification_version && document.classification_version !== 'legacy')}
          />
          <InfoRow
            label="Classification Source"
            value={formatClassificationSource(document.classification_method, document.classification_confidence)}
            verified={Boolean(document.classification_method)}
          />
          <InfoRow label="File Type" value={fileType} verified={Boolean(fileType)} />
          <InfoRow label="Source File" value={sourceFileName || 'Not recorded'} verified={Boolean(sourceFileName)} />
          <InfoRow label="Latest Version" value={latestVersionLabel} verified={Boolean(latestVersion)} />
          <InfoRow label="Version Count" value={`${versionList.length} version${versionList.length === 1 ? '' : 's'}`} verified />
        </InfoCard>

        <InfoCard title="Matter Linkage" icon={<LinkIcon className="h-5 w-5" />}>
          <InfoRow label="Client">
            <div className="mt-1">
              {isEditingClient ? (
                <div className="space-y-2">
                  <select
                    className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:border-transparent focus:ring-2 focus:ring-lex-accent"
                    value={document.client_id || ''}
                    onChange={(e) => onClientUpdate(e.target.value || null)}
                    disabled={isUpdatingDocument}
                  >
                    <option value="">No client assigned</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name}
                      </option>
                    ))}
                  </select>
                  <InlineCancel onClick={() => setIsEditingClient(false)} disabled={isUpdatingDocument} />
                </div>
              ) : (
                <EditableValue
                  value={clientDisplay || 'Not assigned'}
                  detail={hasClientMismatch ? 'Client ID exists, but the loaded client list did not resolve a name.' : undefined}
                  warning={!clientDisplay}
                  disabled={isUpdatingDocument}
                  onEdit={() => setIsEditingClient(true)}
                />
              )}
            </div>
          </InfoRow>

          <InfoRow label="Matter">
            <div className="mt-1">
              {isEditingMatter ? (
                <div className="space-y-2">
                  <select
                    className="w-full rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-3 py-2 text-sm text-[var(--brand-text-primary)] focus:border-transparent focus:ring-2 focus:ring-lex-accent"
                    value={document.matter_id || ''}
                    onChange={(e) => onMatterUpdate(e.target.value || null)}
                    disabled={isUpdatingDocument}
                  >
                    <option value="">No matter assigned</option>
                    {matters.map((matter) => (
                      <option key={matter.id} value={matter.id}>
                        {matter.matter_number} - {matter.description}
                      </option>
                    ))}
                  </select>
                  <InlineCancel onClick={() => setIsEditingMatter(false)} disabled={isUpdatingDocument} />
                </div>
              ) : (
                <EditableValue
                  value={matterDisplay || 'Not assigned'}
                  detail={matterMeta || (hasMatterMismatch
                    ? 'Matter ID exists, but the loaded matter list did not resolve a name.'
                    : isCourtRulesDocument
                      ? 'Court Rules authority documents are available to the Court Rules resolver.'
                      : 'Matter linkage controls AI retrieval scope.')}
                  warning={!matterDisplay}
                  disabled={isUpdatingDocument}
                  onEdit={() => setIsEditingMatter(true)}
                />
              )}
            </div>
          </InfoRow>
          <InfoRow label="AI Retrieval Scope">
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${aiRetrievalScopeClass}`}>
              {aiRetrievalScopeLabel}
            </span>
          </InfoRow>
        </InfoCard>
      </div>

      <DocumentTrackingPanel
        documentId={document.document_id}
        documentTitle={document.title}
        versions={versionList}
      />

      {document.ai_metadata && (
        <div className="mt-6">
          <h5 className="text-md font-medium text-gray-900 dark:text-white mb-3">AI Analysis</h5>
          <div className="bg-lex-accent-soft p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Practice Area</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {document.ai_metadata.classification?.practice_area || 'Not classified'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Processing Status</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {document.ai_metadata.processing_status || 'Unknown'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <section className="min-w-0 rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-4 shadow-sm">
    <div className="mb-4 flex items-center gap-2 text-[var(--brand-text-primary)]">
      <span className="text-[var(--brand-accent-primary)]">{icon}</span>
      <h5 className="font-semibold">{title}</h5>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const InfoRow = ({
  label,
  value,
  verified,
  children,
}: {
  label: string;
  value?: string;
  verified?: boolean;
  children?: React.ReactNode;
}) => (
  <div>
    <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-tertiary)]">
      <span>{label}</span>
      {verified && <CheckCircleIcon className="h-3.5 w-3.5 text-emerald-500" title="Verified from DMS source data" />}
    </div>
    {children || <p className="break-words text-sm text-[var(--brand-text-primary)]">{value || 'Not recorded'}</p>}
  </div>
);

const EditableValue = ({
  value,
  detail,
  warning,
  verified: _verified,
  disabled,
  onEdit,
}: {
  value: string;
  detail?: string;
  warning?: boolean;
  verified?: boolean;
  disabled?: boolean;
  onEdit: () => void;
}) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0 flex-1">
      <p className={`break-words text-sm ${warning ? 'text-amber-700 dark:text-amber-300' : 'text-[var(--brand-text-primary)]'}`}>
        {value}
      </p>
      {detail && <p className="mt-0.5 text-xs text-[var(--brand-text-tertiary)]">{detail}</p>}
    </div>
    <button
      onClick={onEdit}
      className="inline-flex flex-shrink-0 items-center gap-1 text-sm text-lex-accent hover:text-lex-accent-hover"
      disabled={disabled}
    >
      <PencilIcon className="h-3 w-3" />
      <span>Change</span>
    </button>
  </div>
);

const InlineCancel = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <button
    onClick={onClick}
    className="px-2 py-1 text-xs text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)]"
    disabled={disabled}
  >
    Cancel
  </button>
);

const InlineEditorActions = ({
  onCancel,
  disabled,
  saveDisabled,
}: {
  onCancel: () => void;
  disabled?: boolean;
  saveDisabled?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <button
      type="submit"
      className="rounded-md bg-lex-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
      disabled={disabled || saveDisabled}
    >
      Save
    </button>
    <button
      type="button"
      onClick={onCancel}
      className="px-2 py-1 text-xs text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)]"
      disabled={disabled}
    >
      Cancel
    </button>
  </div>
);

const VerificationBadge = ({
  label,
  verified,
  warning,
}: {
  label: string;
  verified?: boolean;
  warning?: boolean;
}) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium ${
    verified && !warning
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
  }`}>
    <CheckCircleIcon className="h-3.5 w-3.5" />
    {label}
  </span>
);

function formatDateTime(value?: string | null): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function describeFileType(mimeType?: string, fileName?: string, fallback?: string): string {
  const mime = String(mimeType || '').toLowerCase();
  const name = String(fileName || '').toLowerCase();
  if (mime.includes('wordprocessingml') || mime.includes('msword') || name.endsWith('.docx') || name.endsWith('.doc')) {
    return name.endsWith('.doc') ? 'Microsoft Word document (.doc)' : 'Microsoft Word document (.docx)';
  }
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF document';
  if (mime.includes('spreadsheet') || name.endsWith('.xlsx') || name.endsWith('.xls')) return 'Spreadsheet';
  if (mime.includes('presentation') || name.endsWith('.pptx') || name.endsWith('.ppt')) return 'Presentation';
  if (mime.includes('text') || name.endsWith('.txt')) return 'Plain text';
  return fallback || mimeType || 'Unknown file type';
}

function formatClassificationSource(method?: string | null, confidence?: string | number | null): string {
  const methodLabel = method ? method.replace(/_/g, ' ') : 'Not recorded';
  if (confidence === null || confidence === undefined || confidence === '') return methodLabel;
  const numeric = Number(confidence);
  if (Number.isNaN(numeric)) return methodLabel;
  return `${methodLabel} · ${Math.round(numeric * 100)}%`;
}
